import { useLocation, useMatch } from 'react-router-dom'
import { IOnboardingStep } from '~/@api/generated-api-types-user/Api'
import type { UserWithMeta } from '~/@biz_account/useUser/types'

type CheckedRouteName =
  | 'order'
  | 'get-started-phone-verification'
  | 'get-started'
  | null

function useOnboarding(user: UserWithMeta | null) {
  const location = useLocation()
  const isOrderRoute = useMatch('/order/:businessId/:stageNumber')
  const isOnboardingPhoneVerificationRoute = useMatch(
    '/get-started/phone-verification'
  )
  const isOnboardingRoute = useMatch('/get-started')

  const routeName: CheckedRouteName = (() => {
    if (isOrderRoute) return 'order'
    if (isOnboardingPhoneVerificationRoute)
      return 'get-started-phone-verification'
    if (isOnboardingRoute) return 'get-started'
    return null
  })()

  const redirectUrl = (() => {
    return _getOnboardingStepRedirectUrl(
      user?.onboarding?.currentRequiredStep ?? null,
      location.pathname as string,
      routeName
    )
  })()

  return {
    isUserApproved: user?.onboarding?.currentRequiredStep === null,
    hasVerifiedPhone:
      user?.onboarding?.currentRequiredStep !==
      IOnboardingStep.PhoneVerification,
    redirectUrl,
  }
}

function _getOnboardingStepRedirectUrl(
  step: IOnboardingStep | null,
  locationPathname: string,
  checkedRouteName: CheckedRouteName
) {
  if (step === null) {
    if (locationPathname.startsWith('/get-started')) return '/'
    return null
  }

  if (
    step === IOnboardingStep.PhoneVerification &&
    checkedRouteName !== 'get-started-phone-verification'
  )
    return '/get-started/phone-verification'

  const onboardingRoute = _getOnboardingStepRoute(step, locationPathname)
  if (onboardingRoute) {
    if (
      locationPathname.startsWith('/get-started') &&
      locationPathname !== onboardingRoute
    )
      return onboardingRoute

    if (checkedRouteName === 'get-started') return onboardingRoute

    if (
      checkedRouteName &&
      ['order', 'project-detail'].includes(checkedRouteName)
    )
      return onboardingRoute
  }

  return null
}

function _getOnboardingStepRoute(step: IOnboardingStep, currentRoute: string) {
  if (step === IOnboardingStep.PhoneVerification) {
    return '/get-started/phone-verification'
  }

  if (step === IOnboardingStep.IdentityVerification) {
    if (currentRoute.startsWith('/get-started/phone-verification')) return '/'
    return '/get-started/identity-verification'
  }

  if (step === IOnboardingStep.IdentityVerificationConfirmed)
    return '/get-started/identity-confirmation'
  if (step === IOnboardingStep.CompanyFormCompletion)
    return '/get-started/company-confirmation'
  if (step === IOnboardingStep.CompanyFormAdminConfirmation)
    return '/get-started/company-confirmation-admin'
  if (step === IOnboardingStep.QuestionnaireCompletion)
    return '/get-started/investor-analysis'
  if (step === IOnboardingStep.ContractSignature)
    return '/get-started/investor-signature'

  return null
}

export { useOnboarding }
